import { Icon, TabBar, type TabItem } from '@uplink/ui';
import { useMemo, useState } from 'react';
import { AdminBroadcastsScreen } from './admin/AdminBroadcastsScreen.tsx';
import { AdminDomainsScreen } from './admin/AdminDomainsScreen.tsx';
import { AdminOrdersScreen } from './admin/AdminOrdersScreen.tsx';
import { AdminPromosScreen } from './admin/AdminPromosScreen.tsx';
import { AdminProvidersScreen } from './admin/AdminProvidersScreen.tsx';
import { AdminScreen } from './admin/AdminScreen.tsx';
import { AdminServerCatalogScreen } from './admin/AdminServerCatalogScreen.tsx';
import { AdminServersScreen } from './admin/AdminServersScreen.tsx';
import { AdminSystemErrorsScreen } from './admin/AdminSystemErrorsScreen.tsx';
import { AdminUserScreen } from './admin/AdminUserScreen.tsx';
import { AdminUsersScreen } from './admin/AdminUsersScreen.tsx';
import { AdminVpnSettingsScreen } from './admin/AdminVpnSettingsScreen.tsx';
import { AdminVpnStatusScreen } from './admin/AdminVpnStatusScreen.tsx';
import { useBackButton } from './native-controls.tsx';
import { initialRoute, type Nav, type Pushed, type Tab } from './nav.ts';
import { usePrefs } from './prefs.tsx';
import { BalanceScreen } from './screens/BalanceScreen.tsx';
import { DomainCheckoutScreen } from './screens/DomainCheckoutScreen.tsx';
import { DomainScreen } from './screens/DomainScreen.tsx';
import { DomainsScreen } from './screens/DomainsScreen.tsx';
import { GiftScreen } from './screens/GiftScreen.tsx';
import { HomeScreen } from './screens/HomeScreen.tsx';
import { ProfileScreen } from './screens/ProfileScreen.tsx';
import { ServerCreateScreen } from './screens/ServerCreateScreen.tsx';
import { ServerScreen } from './screens/ServerScreen.tsx';
import { ServersScreen } from './screens/ServersScreen.tsx';
import { TopUpScreen } from './screens/TopUpScreen.tsx';
import { VpnSubscriptionScreen } from './screens/VpnSubscriptionScreen.tsx';
import { VpnTrialScreen } from './screens/VpnTrialScreen.tsx';
import { haptic, inTelegram } from './telegram.ts';
import { DesktopHome, useWide } from './web/DesktopHome.tsx';
import { WebSidebar } from './web/WebSidebar.tsx';

const TAB_ICONS: Pick<TabItem<Tab>, 'id' | 'icon'>[] = [
  { id: 'home', icon: 'home' },
  { id: 'domains', icon: 'globe' },
  { id: 'servers', icon: 'server' },
  { id: 'balance', icon: 'wallet' },
  { id: 'profile', icon: 'user' },
];

function PushedScreen({ screen, nav }: { screen: Pushed; nav: Nav }) {
  switch (screen.name) {
    case 'topup':
      return <TopUpScreen nav={nav} initialCents={screen.amountCents} after={screen.after} />;
    case 'domainCheckout':
      return <DomainCheckoutScreen nav={nav} offer={screen} />;
    case 'domain':
      return <DomainScreen nav={nav} id={screen.id} />;
    case 'serverCreate':
      return <ServerCreateScreen nav={nav} vpn={screen.vpn} />;
    case 'server':
      return <ServerScreen nav={nav} id={screen.id} />;
    case 'vpnTrial':
      return <VpnTrialScreen />;
    case 'vpnSubscription':
      return <VpnSubscriptionScreen nav={nav} />;
    case 'admin':
      return <AdminScreen nav={nav} />;
    case 'adminUsers':
      return <AdminUsersScreen nav={nav} />;
    case 'adminDomains':
      return <AdminDomainsScreen nav={nav} />;
    case 'adminServers':
      return <AdminServersScreen nav={nav} />;
    case 'adminUser':
      return <AdminUserScreen nav={nav} id={screen.id} />;
    case 'adminOrders':
      return <AdminOrdersScreen nav={nav} filter={screen.filter} />;
    case 'adminBroadcasts':
      return <AdminBroadcastsScreen />;
    case 'adminPromos':
      return <AdminPromosScreen />;
    case 'adminProviders':
      return <AdminProvidersScreen />;
    case 'adminServerCatalog':
      return <AdminServerCatalogScreen />;
    case 'adminVpnSettings':
      return <AdminVpnSettingsScreen />;
    case 'adminVpnStatus':
      return <AdminVpnStatusScreen />;
    case 'adminSystemErrors':
      return <AdminSystemErrorsScreen />;
    case 'gift':
      return <GiftScreen nav={nav} />;
  }
}

export function App({ web = false }: { web?: boolean }) {
  const { t } = usePrefs();
  const tabs = TAB_ICONS.map((it) => ({ ...it, label: t.tabs[it.id] }));
  // Opened from a bot button: start on the screen it points to
  const [start] = useState(() => initialRoute(window.location.search));
  const [tab, setTab] = useState<Tab>(start.tab);
  const [stack, setStack] = useState<Pushed[]>(start.stack);
  const top = stack.at(-1);

  const nav: Nav = useMemo(
    () => ({
      tab: (t) => {
        haptic.select();
        setStack([]);
        setTab(t);
      },
      push: (s) => {
        haptic.tap();
        setStack((st) => [...st, s]);
      },
      back: () => setStack((st) => st.slice(0, -1)),
      replace: (s) => setStack((st) => [...st.slice(0, -1), s]),
      reset: (t = 'home') => {
        setStack([]);
        setTab(t);
      },
    }),
    [],
  );

  useBackButton(top ? nav.back : null);
  const wide = useWide();
  const wideWeb = web && wide;

  const screens = (
    <>
      {/* The tab stays mounted under pushed screens, so a search query survives going back */}
      <div className="ul-app" key={tab} hidden={!!top}>
        {tab === 'home' ? wideWeb ? <DesktopHome nav={nav} /> : <HomeScreen nav={nav} /> : null}
        {tab === 'domains' ? <DomainsScreen nav={nav} initialQuery={start.query} /> : null}
        {tab === 'servers' ? <ServersScreen nav={nav} /> : null}
        {tab === 'balance' ? <BalanceScreen nav={nav} /> : null}
        {tab === 'profile' ? <ProfileScreen nav={nav} /> : null}
        {top ? null : (
          <div className="ul-tabbar-fixed">
            <TabBar items={tabs} active={tab} onChange={nav.tab} />
          </div>
        )}
      </div>
      {top ? (
        <div className="ul-app" key={`${stack.length}-${top.name}`}>
          {!inTelegram ? (
            <div className="ul-backbar">
              <button type="button" className="ul-backbar__btn" onClick={nav.back}>
                <Icon name="chevron" size={18} className="ul-flip" />
                {t.common.back}
              </button>
            </div>
          ) : null}
          <PushedScreen screen={top} nav={nav} />
        </div>
      ) : null}
    </>
  );
  if (!web) return screens;
  // The website on a wide screen: a sidebar instead of the bottom tab bar
  return (
    <div className="ul-web-shell">
      <WebSidebar tabs={tabs} active={tab} nav={nav} />
      <div className="ul-web-main">{screens}</div>
    </div>
  );
}
